import React, { useState, useEffect, useRef, useCallback } from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { PDFDocument } from 'pdf-lib';
import * as pdfjs from 'pdfjs-dist';
import {
  Button,
  Checkbox,
  Loader,
  FileUploader,
  PopupMenu
} from 'quantux-ui';
import ThumbnailsPanel from '../thumbnails-panel';
import WarningDialog from '../warning-dialog';
import { blue, gray, salmon } from '../../theme';
import {
  BASE_URL,
  ALLOWED_IMG_FORMATS,
  FILE_UPLOAD_MAX_SIZE,
  FLAG_VALUE,
  FORM_TYPE,
  findPageById,
  equal,
  checkWebPSupport,
} from '../../utils';
import CriticalWarningIcon from '../svg-icons/critical-warning-icon';
import WarningIcon from '../svg-icons/warning-icon';
//
import DocWriterWrapper from '../doc-writer/doc-writer-wrapper';
import QuantuxImageViewer from '../quantux-image-viewer';
const SCALING_FACTOR = 0.05;
const MIN_SCALE = 0.3; // Minimum zoom scale
const MAX_SCALE = 5; // Maximum zoom scale

// Initialize pdf.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

// Helper function to calculate size of data URL
function getDataUrlSize(dataUrl) {
  // Remove the data URL prefix to get just the base64 string
  const base64 = dataUrl.split(',')[1];
  // Convert base64 to raw binary size
  return (base64.length * 3) / 4;
}

/**
 * Converts a PDF page to an image at specified scale
 * @param {Object} page - PDF.js page object
 * @param {number} scale - Scale factor for rendering (1 = 100%)
 * @returns {Promise<string>} Base64 encoded image string
 */
async function pageToImage(page, scale) {
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  canvas.width = viewport.width;
  canvas.height = viewport.height;

  await page.render({
    canvasContext: context,
    viewport,
  }).promise;

  if (checkWebPSupport()) {
    const imageData = canvas.toDataURL('image/webp');
    const imageSize = getDataUrlSize(imageData);
    // console.log(`❤️‍🔥❤️‍🔥❤️‍🔥 WebP Image size: ${(imageSize / 1024).toFixed(2)} KB`);
    // return canvas.toDataURL('image/webp');
    return {
      imageData,
      imageSize
    };
  }

  const imageData = canvas.toDataURL('image/jpeg');
  const imageSize = getDataUrlSize(imageData);
  // console.log(`❤️‍🔥❤️‍🔥❤️‍🔥 JPEG Image size: ${(imageSize / 1024).toFixed(2)} KB`);
  // return canvas.toDataURL('image/jpeg');
  return {
    imageData,
    imageSize
  };
}

const CaseDoc = (props) => {
  const [formName, setFormName] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [readOnlyMode, setReadOnlyMode] = useState(false);
  // ---
  const [caseDocuments, setCaseDocuments] = useState([]);
  const [currentPageId, setCurrentPageId] = useState('');
  const [docPageImageSrc, setDocPageImageSrc] = useState('');
  const [isNone, setIsNone] = useState(false);
  const [isUploadingCaseDocument, setIsUploadingCaseDocument] = useState(false);
  const [showThumbnails, setShowThumbnails] = useState(false);
  // --- Error message dialog
  const [errorMessage, setErrorMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  // -- Warning message dialog
  const [warningMessage, setWarningMessage] = useState('');
  const [showDocumentDeleteWarning, setShowDocumentDeleteWarning] = useState(false);
  const [showPageDeleteWarning, setShowPageDeleteWarning] = useState(false);
  // --- Document delete menu
  const [showDocumentDeleteMenu, setShowDocumentDeleteMenu] = useState(false);
  // -- Image scaling (pinch zoom)
  const [scale, setScale] = useState(1);
  const [startDistance, setStartDistance] = useState(null);
  // -- Fileuploader status message
  const [fileUploadStatusMsg, setFileUploadStatusMsg] = useState('Uploading ...');
  const [fileUploadProgress, setFileUploadProgress] = useState(0);

  // ----- Props --------------------------------------------------------------
  const {
    caseDocs = [],
    isNone: isNoneCaseDocValue = false,
    formType,
    scheduleId
  } = props;

  // ----- Refs ---------------------------------------------------------------
  const documentRef = useRef();
  const documentDeleteMenuRef = useRef();
  const imageRef = useRef();
  const docWriterWrapperRef = useRef();

  // ----- Effects -----------------------------------------------------------

  // -- Current page id
  useEffect(() => {
    if (Array.isArray(caseDocuments) && caseDocuments.length > 0 && currentPageId.length > 0) {
      const tmpIndex = caseDocuments.findIndex(el => el.id === currentPageId);
      if (tmpIndex > -1) {
        setDocPageImageSrc(caseDocuments[tmpIndex].compositeImage);
        setCurrentPage(tmpIndex + 1);
      }
    }
  }, [currentPageId]);

  // --- props.caseDocs effect (initial data load)
  useEffect(() => {
    if (!equal(caseDocuments, caseDocs) || isNone !== isNoneCaseDocValue) {
      setCaseDocuments(caseDocs);
      setIsNone(isNoneCaseDocValue);
      if (currentPageId === '' && caseDocs.length > 0) {
        setCurrentPageId(caseDocs[0].id);
      }

      const tmpIndex = findPageById(caseDocuments, currentPageId);
      if (tmpIndex > -1 && tmpIndex < caseDocs.length) {
        setCurrentPageId(caseDocs[tmpIndex].id);
        setDocPageImageSrc(caseDocs[tmpIndex].compositeImage);
        setCurrentPage(tmpIndex + 1);
      } else {
        if (caseDocs.length === 0) {
          setCurrentPageId('');
          setDocPageImageSrc('');
          setCurrentPage(0);
        } else {
          setCurrentPageId(caseDocs[0].id);
          setDocPageImageSrc(caseDocs[0].compositeImage);
          setCurrentPage(1);
        }
      }
    }
  }, [caseDocs, isNoneCaseDocValue]);

  // --- Form name from form type prop value
  useEffect(() => {
    let value = '';
    switch (props.formType) {
      case FORM_TYPE.faceSheet:
        value = 'Face Sheet';
        break;
      case FORM_TYPE.recordsLabs:
        value = 'Labs';
        break;
      case FORM_TYPE.recordsEkg:
        value = 'EKG';
        break;
      case FORM_TYPE.recordsImaging:
        value = 'Imaging'
        break;
      case FORM_TYPE.recordsConsults:
        value = 'Consults'
        break;
      case FORM_TYPE.consentsAnesthesia:
        value = 'Anesthesia Consent';
        break;
      case FORM_TYPE.consentsSurgery:
        value = 'Surgery Consent';
        break;
      case FORM_TYPE.nursing:
        value = 'Nursing';
        break;
      case FORM_TYPE.nursingPreOp:
        value = 'Nursing PreOp Forms';
        break;
      case FORM_TYPE.nursingIntraOp:
        value = 'Nursing IntraOp Forms';
        break;
      case FORM_TYPE.nursingPostOp:
        value = 'Nursing PostOp Forms';
        break;
      case FORM_TYPE.surgeon:
        value = 'Surgeon';
        break;
      case FORM_TYPE.surgeonPreOp:
        value = 'Surgeon PreOp Forms';
        break;
      case FORM_TYPE.surgeonIntraOp:
        value = 'Surgeon IntraOp Forms';
        break;
      case FORM_TYPE.surgeonPostOp:
        value = 'Surgeon PostOp Forms';
        break;
      case FORM_TYPE.anesthesiaPreOp:
        value = 'Anesthesia Pre Op Form';
        break;
      case FORM_TYPE.anesthesiaIntraOp:
        value = 'Anesthesia Intra Op Form';
        break;
      case FORM_TYPE.anesthesiaPostOp:
        value = 'Anesthesia Post Op Form';
        break;
      case FORM_TYPE.ordersPreOp:
        value = 'Orders Pre Op Form';
        break;
      case FORM_TYPE.ordersIntraOp:
        value = 'Orders Intra Op Form';
        break;
      case FORM_TYPE.ordersPostOp:
        value = 'Orders Post Op Form';
        break;
      case FORM_TYPE.discharge:
        value = 'Discharge Docs';
        break;
      default:
        value = ''
    }
    if (formName !== value) {
      setFormName(value);
    }
  }, [props.formType]);

  // Case documents and selected page effect
  useEffect(() => {
    if (caseDocuments.length > 0) {
      const tmpIndex = findPageById(caseDocuments, currentPageId);
      if (docPageImageSrc !== caseDocuments[tmpIndex].compositeImage) {
        setDocPageImageSrc(caseDocuments[tmpIndex].compositeImage);
      }
    }
  }, [currentPageId]);

  // ----- Styles -------------------------------------------------------------
  const styles = StyleSheet.create({
    caseDocContainer: {
      position: 'relative',
      height: '100%'
    },
    uploadBtnContainer: {
      width: window.innerWidth <= 720 ? '320px' : '430px',
      padding: '16px'
    },
    uploadBtnContainerTop: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    uploadBtnContainerBottom: {
      marginTop: '8px',
      borderRadius: '4px',
      width: '100%',
      height: '42px',
      alignItems: 'center',
      justifyContent: 'center'
    },
    uploadBtnContainerBottomDisabled: {
      opacity: '0.5'
    },
    toolbar: {
      height: '40px',
      background: blue[700],
      borderTop: '1px black',
      borderBottom: '1px black',
      padding: '0 8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    thumbnailsIcon: {
      paddingTop: '6px',
      position: 'relative'
    },
    toolbarIcon: {
      fontSize: '22px',
      color: gray[300],
      fill: '#96C8FA',
      cursor: 'pointer',
      ':hover': {
        fill: blue[400],
        color: blue[400]
      }
    },
    badge: {
      position: 'absolute',
      top: '18px',
      right: '0px',
      padding: '0 4px',
      fontSize: '12px',
      fontWeight: 'bold',
      background: blue[500],
      borderRadius: '4px',
      color: 'white',
      cursor: 'pointer'
    },
    pageSelector: {
      display: 'flex',
      height: '30px',
      width: '120px',
      border: `1px solid ${gray[800]}`,
      borderRadius: '5px',
      marginLeft: '16px'
    },
    pageSelectorBtn: {
      fontSize: '12px',
      width: '30px',
      height: '30px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: blue[800]
    },
    pageSelectorPageNumberDisplay: {
      width: '80x',
      background: 'black',
      color: gray[300],
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexGrow: '1',
      height: '30px'
    },
    searchPopupMenuContainer: {
      width: '180px',
      background: blue[900],
      paddingBottom: '1px',
      borderRadius: '5px',
      overflowY: 'auto',
      maxHeight: '480px'
    },
    menuItem: {
      cursor: 'pointer',
      background: gray[900],
      color: gray[300],
      padding: '4px',
      lineHeight: '16px',
      fontSize: '14px',
      marginTop: '1px',
      height: '40px',
      ':hover': {
        background: '#184878',
        color: 'white'
      },
      ':last-child': {
        borderBottom: '0'
      }
    },
    menuItemDanger: {
      color: salmon[500]
    },
    ellipsisBtn: {
      position: 'relative',
      width: '24px',
      height: '24px',
      background: blue[200],
      color: gray[600],
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '2px'
    },
    menuItemIcon: {
      minWidth: '28px'
    },
    menuItemText: {
      paddingLeft: '8px'
    },
    docPageContainer: {
      position: 'relative',
      height: 'calc(100% - 40px)',
      width: '100%',
      maxWidth: '100vw',
      background: 'black',
      padding: '8px',
      margin: '0 auto',
      overflow: 'auto'
      // touchAction: 'none',
      // overflow: 'hidden',
    },
    docPageImage: {
      maxWidth: '100%',
      width: '100%',
      height: 'auto',
      transformOrigin: 'center'
      // transition: 'transform 0.1s ease-out'
    },
    thumbnailsDrawer: {
      display: 'flex',
      flexFlow: 'column',
      position: 'absolute',
      width: '124px',
      overflowY: 'auto',
      background: blue[900],
      borderRight: `1px solid ${blue[600]}`,
      zIndex: '1'
    },
    groupIconContainer: {
      height: '24px',
      borderRadius: '4px',
      background: '#96C8FF',
      ':hover': {
        background: '#00A0FF',
      }
    },
  });

  // ----- Event handlers -----------------------------------------------------
  const handleOnChangeIsNone = () => {
    const updatedIsChecked = !isNone;
    setIsNone(updatedIsChecked);
    props.onChangeIsNone(updatedIsChecked);
  };

  const handleOnClickScanDocs = () => {
    props.onScanDocument({
      formType,
      caseDocs,
    });
  };

  const uploadFileWithProgress = (url, formData, token, onProgress) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Upload progress event
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const percentComplete = (event.loaded / event.total) * 100;
          // onProgress(percentComplete);
          onProgress({
            progress: percentComplete,
            fileSize: event.total,
            uploadSize: event.loaded
          });
        }
      });

      // Load completion event
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(xhr.response);
        } else {
          reject(new Error(`HTTP Error: ${xhr.status}`));
        }
      });

      // Error event
      xhr.addEventListener('error', () => reject(new Error('Network Error')));

      // Send the request
      xhr.open('POST', url);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.responseType = 'json';
      xhr.send(formData);
    });
  };

  async function convertPdfToImages(pdfBuffer) {
    try {
      // Load the PDF using pdf.js
      const pdf = await pdfjs.getDocument({ data: pdfBuffer }).promise;
      const totalPages = pdf.numPages;
      const result = [];
      const thumbNails = [];
      const fullImages = [];

      // Process each page
      let totalSize = 0;
      for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const percentDone = Math.round((pageNum / totalPages) * 100);
        setFileUploadProgress(percentDone);

        // Generate thumbnail (scaled down version)
        const thumbNailData = await pageToImage(page, 0.3); // 30% of original size
        const {
          imageData: tbData,
          imageSize: tbSize
        } = thumbNailData;
        // thumbNails.push(thumbNail);
        thumbNails.push(tbData);

        // Generate full-size image
        const fullImageData = await pageToImage(page, 1.0); // 100% of original size
        const {
          imageData: fdData,
          imageSize: fdSize
        } = fullImageData;
        totalSize += fdSize;
        // fullImages.push(fullImage);
        fullImages.push(fdData);

        result.push({
          thumbnail: tbData,
          fullImage: fdData,
        });
      }

      console.log(`❤️‍🔥❤️‍🔥❤️‍🔥❤️‍🔥 Total size = ${(totalSize / 1024).toFixed(2)} KB`);

      return {
        thumbNailsAndImages: result,
        thumbNails,
        fullImages
      };
    } catch (error) {
      console.error('Error converting PDF to images:', error);
      throw error;
    }
  }

  const handleOnDocumentDrop = async (img) => {
    // Check for supported image formats and max file size
    const isSupportedFileType = ALLOWED_IMG_FORMATS.findIndex(el => el === img.type) > -1;
    const isSupportedFileSize = img.size < FILE_UPLOAD_MAX_SIZE;

    if (!isSupportedFileType || !isSupportedFileSize) {
      setErrorMessage(
        !isSupportedFileType
          ? 'Only PNG, JPEG and PDF file are supported!'
          : 'Try uploading a file with a size of 5MB or less!'
      );
      setShowErrorMessage(true);
      return;
    }

    // Compress and upload case document
    setIsUploadingCaseDocument(true);

    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);

    if (img.type === 'image/jpeg' || img.type === 'image/png') {
      const reader = new FileReader();
      reader.readAsDataURL(img);

      reader.onload = function (e) {
        const image = new Image();
        image.src = e.target.result;

        image.onload = function () {
          const width = image.width;
          const height = image.height;
          const maxWidth = width > 1920 ? Math.floor(width / 2) : width
          const maxHeight = height > 1080 ? Math.floor(height / 2) : height;

          /* eslint-disable */
          // Compress dropped image and upload
          new Compressor(img, {
            quality: 0.5,
            maxWidth: maxWidth,
            maxHeight: maxHeight,
            resize: 'cover',
            success: (result) => {
              const formData = new FormData();
              formData.append('file', result);
              formData.append('formType', props.formType);
              formData.append('scheduleId', props.scheduleId);

              const options = {
                method: 'POST',
                body: formData,
                redirect: 'follow',
                headers: reqHeaders
              };

              fetch(`${BASE_URL}/casesSchedule/uploadCaseDoc`, options)
                .then(response => response.text())
                .then(result => {
                  const res = JSON.parse(result);
                  if (res.success) {
                    const {
                      fileName,
                      fileUrl,
                      compositeImage,
                      caseDocId
                    } = res;

                    const updatedCaseDocuments = caseDocuments.concat({
                      id: caseDocId,
                      fileName,
                      imageUrl: fileUrl,
                      compositeImage: compositeImage
                    });
                    setCaseDocuments(updatedCaseDocuments);
                    // setCaseDocuments((prvState) => prvState.concat({
                    //   id: caseDocId,
                    //   fileName,
                    //   imageUrl: fileUrl,
                    //   compositeImage: compositeImage
                    // }));

                    setCurrentPageId(caseDocId);
                    setIsUploadingCaseDocument(false);
                    props.onCaseDocUploaded(updatedCaseDocuments);
                  }
                })
                .catch(error => console.log('error', error));
            },
            error: (err) => {
              console.log(err.message);
              setErrorMessage(err.message);
              setShowErrorMessage(true);
            }
            /* eslint-enable */
          })
        }
      }
    }

    if (img.type === 'application/pdf') {
      setFileUploadStatusMsg('Optimizing PDF file...');

      // Convert File object to ArrayBuffer for optimization
      const inputBuffer = await img.arrayBuffer();
      const data = await convertPdfToImages(inputBuffer);
      const { fullImages } = data;
      const pdfImages = fullImages.map(el => ({
        id: Math.random().toString(36).slice(2),
        compositeImage: el
      }));

      /*
      // const optimizer = new PDFOptimizer();
      // const optimizedBuffer = await optimizer.optimizePDF(inputBuffer, pdfOptions);
      // const optimizedBuffer = await optimizePDFFile(inputBuffer);
      const optimizedBuffer = await compressPDF(inputBuffer);

      // Convert optimized buffer back to File object
      const optimizedFile = new File(
        [optimizedBuffer],
        img.name,
        { type: 'application/pdf' }
      );

      console.log(`❤️‍🔥❤️‍🔥❤️‍🔥 Optmized PDF file size = ${((optimizedFile.size) / (1024 * 1024)).toFixed(2)}MB`);
      const formData = new FormData();
      // formData.append('file', img);
      formData.append('file', optimizedFile);
      formData.append('formType', props.formType);
      formData.append('scheduleId', props.scheduleId);

      const options = {
        method: 'POST',
        body: formData,
        redirect: 'follow',
        headers: reqHeaders
      };

      try {
        const result = await uploadFileWithProgress(
          `${BASE_URL}/casesSchedule/uploadPDF`,
          formData,
          token,
          (uploadData) => {
            const { progress, fileSize, uploadSize } = uploadData;
            // console.log(`📤 Upload progress: ${progress.toFixed(2)}%`);
            if (+progress.toFixed(2) >= 99) {
              setFileUploadStatusMsg('Processing PDF file ...');
            } else {
              setFileUploadStatusMsg(`📤 Uploading file ... ${uploadSize.toFixed(2)}/${fileSize.toFixed(2)}(${progress.toFixed(2)}%)`);
              setFileUploadProgress(progress);
            }
          }
        );

        if (result.success) {
          const updatedCaseDocuments = caseDocuments.concat(result.images);
          setCaseDocuments(updatedCaseDocuments);
          setCurrentPageId(result.images[0].id);
          setIsUploadingCaseDocument(false);
          props.onCaseDocUploaded(updatedCaseDocuments);
        }
      } catch (error) {
        console.log('File upload failed!');
        console.error('Error:', error);
        setIsUploadingCaseDocument(false);
      }

      fetch(`${BASE_URL}/casesSchedule/uploadPDF`, options)
        .then(response => response.text())
        .then(result => {
          const res = JSON.parse(result);
          if (res.success) {
            // setCaseDocuments(prvState => prvState.concat(res.images));
            const updatedCaseDocuments = caseDocuments.concat(res.images);
            setCaseDocuments(updatedCaseDocuments);
            setCurrentPageId(res.images[0].id);
            setIsUploadingCaseDocument(false);
            props.onCaseDocUploaded(updatedCaseDocuments);
          }
        })
        .catch(error => console.log('error', error));
      */
      const updatedCaseDocuments = caseDocuments.concat(pdfImages);
      setCaseDocuments(updatedCaseDocuments);
      setCurrentPageId(pdfImages[0].id);
      setIsUploadingCaseDocument(false);
      props.onCaseDocUploaded(updatedCaseDocuments);
    }
  };

  const handleOnDocumentFileSelected = () => { };

  const handleOnEditDocument = () => {
    props.onEdit({
      scheduleId,
      currentPageId,
      formType,
      formName,
      caseDocuments
    });
  };

  const handleOnClickActivateDrawMode = () => {
    props.onShowDocWriter({
      formType: props.formType,
      caseDocs: caseDocuments,
      selectedPageId: currentPageId,
      scheduleId,
      currentPageId,
      formName,
      caseDocuments
    });
  };

  /*
  // QuantuxImageViewer event handlers & helper functions
  // Image Pinch and zoom function without zooming the webpage
  const _calculateDistance = (touch1, touch2) => {
    return Math.sqrt(
      Math.pow(touch2.pageX - touch1.pageX, 2) +
      Math.pow(touch2.pageY - touch1.pageY, 2)
    );
  };

  const calculateDistance = (touch1, touch2) => {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const debouncedSetScale = useCallback(
    debounce((newScale) => {
      setScale(newScale);
      if (imageRef.current) {
        imageRef.current.style.transform = `scale(${newScale})`;
        imageRef.current.style.transformOrigin = "center center";
      }
    }, 50), // Adjust the delay as needed
    []
  );

  const handleTouchStart = (event) => {
    if (event.touches.length === 2) {
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      const initialDistance = calculateDistance(touch1, touch2);
      setStartDistance(initialDistance); // Set the starting distance
    }
  };

  const handleTouchMove = (event) => {
    if (event.touches.length === 2 && startDistance) {
      event.preventDefault();
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      const currentDistance = calculateDistance(touch1, touch2);

      // Calculate new scale
      // const newScale = scale * (currentDistance / startDistance);

      // Calculate new scale with reduced sensitivity
      const distanceChange = (currentDistance - startDistance) * SCALING_FACTOR;
      // const newScale = Math.min(Math.max(scale + distanceChange / startDistance, 0.3), 5);
      let newScale = scale + distanceChange / startDistance;
      newScale = Math.max(MIN_SCALE, Math.min(newScale, MAX_SCALE));

      console.log(`Value of newScale = ${newScale}`);
      setScale(newScale); // Update the scale state
      if (imageRef.current) {
        imageRef.current.style.transform = `scale(${newScale})`;
        imageRef.current.style.transformOrigin = "center center";
      }
    }
  };

  const handleTouchEnd = () => {
    setStartDistance(null); // Reset the starting distance
  };

  const __handleTouchStart = (event) => {
    if (event.touches.length === 2) {
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      const initialDistance = calculateDistance(touch1, touch2);
      setStartDistance(initialDistance); // Set the starting distance
    }
  };

  const __handleTouchMove = (event) => {
    if (event.touches.length === 2 && startDistance) {
      event.preventDefault();
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      const currentDistance = calculateDistance(touch1, touch2);

      // Calculate new scale with sensitivity adjustment
      const distanceChange = (currentDistance - startDistance) * SCALING_FACTOR;
      let newScale = scale + distanceChange / startDistance;

      // Apply bounds to the scale
      newScale = Math.max(MIN_SCALE, Math.min(newScale, MAX_SCALE));

      debouncedSetScale(newScale); // Use debounced scaling
    }
  };

  const __handleTouchEnd = () => {
    setStartDistance(null); // Reset the starting distance
  };
  */

  // ----- Document flag ------------------------------------------------------
  let flag = null;
  if (props.flagType === FLAG_VALUE.critical) {
    flag = <CriticalWarningIcon height={20} />;
  }

  if (props.flagType === FLAG_VALUE.warning) {
    flag = <WarningIcon height={20} />;
  }

  return (
    <div className={css(styles.caseDocContainer)}>
      {/* No case documents uploaded */}
      {caseDocuments.length === 0 &&
        <div className={css(styles.uploadBtnContainer)}>
          {/* Case document form name and isNone checkbox */}
          <div className={css(styles.uploadBtnContainerTop)}>
            <div className='fx fx__v-center'>
              <div style={{ display: props.showFlag ? '' : 'none' }}>
                {!isNone
                  ? flag
                  : <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none">
                    <path
                      fill="#96C8FF"
                      fillRule="evenodd"
                      d="M8 0C3.584 0 0 3.584 0 8s3.584 8 8 8 8-3.584 8-8-3.584-8-8-8ZM1.714 8.029c0-3.452 2.855-6.248 6.379-6.248 1.475 0 2.83.492 3.907 1.32l-8.938 8.754A6.091 6.091 0 0 1 1.714 8.03ZM4 12.904a6.385 6.385 0 0 0 3.907 1.32c3.524 0 6.379-2.796 6.379-6.247a6.091 6.091 0 0 0-1.348-3.827L4 12.904Z"
                      clipRule="evenodd"
                    />
                  </svg>
                }
              </div>
              <div
                className='ml-2'
                style={{ marginBottom: '4px' }}
              >
                {formName}
              </div>
            </div>
            <Checkbox
              text='None'
              isDisabled={readOnlyMode}
              isChecked={isNone}
              onChange={handleOnChangeIsNone}
            />
          </div>
          {/* Scan document button and file uploader */}
          <div className={css(styles.uploadBtnContainerBottom, isNone && styles.uploadBtnContainerBottomDisabled)}>
            {isUploadingCaseDocument
              ? <Loader
                iconType="progressBar"
                progress={fileUploadProgress}
                iconSize="2x"
                text={fileUploadStatusMsg}
              />
              : <>
                {/* Scan document button */}
                <Button
                  btnType='primary'
                  style={{
                    margin: '0',
                    padding: '0',
                    minHeight: '40px',
                    maxHeight: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onClick={handleOnClickScanDocs}
                >
                  <div className='fx fx__hv-center'>
                    <svg
                      width="32"
                      height="32"
                      fill="none"
                      style={{ marginRight: '8px' }}
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M1.286 9.286c.71 0 1.285-.575 1.285-1.286V2.571H8A1.285 1.285 0 1 0 8 0H2.571A2.574 2.574 0 0 0 0 2.571V8c0 .71.575 1.286 1.286 1.286ZM8 29.429H2.571V24A1.285 1.285 0 1 0 0 24v5.429A2.574 2.574 0 0 0 2.571 32H8a1.285 1.285 0 1 0 0-2.571ZM24 0h5.429A2.574 2.574 0 0 1 32 2.571V8a1.285 1.285 0 1 1-2.571 0V2.571H24A1.285 1.285 0 1 1 24 0Zm6.714 22.714c-.71 0-1.285.575-1.285 1.286v5.429H24A1.285 1.285 0 1 0 24 32h5.429A2.574 2.574 0 0 0 32 29.429V24c0-.71-.575-1.286-1.286-1.286ZM17.774 5.78l5.791 5.844c.28.28.436.66.436 1.056v12.49c0 .829-.671 1.5-1.5 1.5h-13c-.83 0-1.5-.671-1.5-1.5V6.834c0-.83.67-1.5 1.5-1.5h7.208c.21 0 .416.046.606.13l.013.005a1.265 1.265 0 0 1 .137.07c.11.066.215.145.308.239Zm-.273 6.058h4.162l-4.162-4.2v4.2Zm5 13.331h-13V6.835H16v5c0 .83.67 1.5 1.5 1.5h5v11.833Z"
                        fill="#fff"
                      />
                    </svg>
                    Scan Documents
                  </div>
                </Button>
                {/* File uploader - drag 'n drop image or click to to select image */}
                <FileUploader
                  text='OR Drag & Drop OR Select File'
                  readOnlyMode={isNone}
                  accept='.png,.jpg,.jpeg,.pdf'
                  // accept='application/pdf'
                  multiple='multiple'
                  showOrLineBreak={false}
                  style={{
                    marginTop: '16px',
                    height: '40px',
                    color: blue[400]
                  }}
                  parentRef={documentRef}
                  onDrop={handleOnDocumentDrop}
                  onChange={handleOnDocumentFileSelected}
                />
              </>
            }
          </div>
        </div>
      }

      {/* ------ Page delete warning dialog ------ */}
      {showPageDeleteWarning &&
        <WarningDialog
          show
          title='Warning'
          message={warningMessage}
          onCancel={() => setShowPageDeleteWarning(false)}
          onContinue={() => {
            setShowPageDeleteWarning(false);
            props.onDeletePage(currentPageId);
          }}
        />
      }

      {/* ------ Document delete warning dialog ------ */}
      {showDocumentDeleteWarning &&
        <WarningDialog
          show
          title='Warning'
          message={warningMessage}
          onCancel={() => setShowDocumentDeleteWarning(false)}
          onContinue={() => {
            setShowDocumentDeleteWarning(false);
            props.onDeleteDocument();
          }}
        />
      }

      {/* Error message dialog */}
      {showErrorMessage &&
        <WarningDialog
          show
          title='File Upload Error'
          message={errorMessage}
          onCancel={() => setShowErrorMessage(false)}
          onContinue={() => setShowErrorMessage(false)}
        />
      }

      {/* Case documents */}
      {caseDocuments.length > 0 &&
        <>
          {/* Toolbar */}
          <div className={css(styles.toolbar)}>
            {/* Left end of the document toolbar */}
            <div className='fx fx__hv-center'>
              <div
                className={css(styles.thumbnailsIcon)}
                onClick={() => setShowThumbnails(!showThumbnails)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="30"
                  fill="none"
                  className={css(styles.toolbarIcon)}
                >
                  <path
                    fillRule="evenodd"
                    d="M29.285 0H2.215C.99 0 0 1.258 0 2.813v24.375C0 28.741.99 30 2.215 30h27.07c1.224 0 2.215-1.258 2.215-2.813V2.813C31.5 1.258 30.51 0 29.285 0ZM12 27H3V3h9v24Zm3 0h13.5V3c0 .085-.01.142-.022.142H15.022C15.009 3.142 15 3.071 15 3v24Zm-4.5-16.5h-6v-6h6v6Zm-6 7.5h6v-6h-6v6Z"
                    clipRule="evenodd"
                  />
                </svg>
                {/* Badge */}
                <div className={css(styles.badge)}>
                  {caseDocuments.length}
                </div>
              </div>
              {/* Page Selector */}
              {caseDocuments.length > 0 &&
                <div className={`ml-2 ${css(styles.pageSelector)}`}>
                  {/* Go Back */}
                  <div
                    className={css(styles.pageSelectorBtn)}
                    style={{
                      fontSize: '18px',
                      borderTopLeftRadius: '5px',
                      borderBottomLeftRadius: '5px',
                      color: '#5080B0'
                    }}
                    onClick={() => {
                      if (currentPage > 1) {
                        const tmpIndex = currentPage - 2;
                        setDocPageImageSrc(caseDocuments[tmpIndex].compositeImage);
                        setCurrentPage(tmpIndex + 1);
                      }
                    }}
                  >
                    <i className='fa fa-minus-circle' />
                  </div>
                  {/* Current Page */}
                  <div className={css(styles.pageSelectorPageNumberDisplay)}>
                    {currentPage}
                  </div>
                  {/* Go Forward */}
                  <div
                    className={css(styles.pageSelectorBtn)}
                    style={{
                      color: '#5080B0',
                      fontSize: '18px',
                      borderTopRightRadius: '5px',
                      borderBottomRightRadius: '5px'
                    }}
                    onClick={() => {
                      if (currentPage < caseDocuments.length) {
                        const tmpIndex = currentPage;
                        setDocPageImageSrc(caseDocuments[tmpIndex].compositeImage);
                        setCurrentPage(tmpIndex + 1);
                      }
                    }}
                  >
                    <i className='fa fa-plus-circle' />
                  </div>
                </div>
              }
            </div>
            {/* Right end of the document toolbar */}
            <div className='fx fx__hv-center'>
              {/* Pencil Icon */}
              {!props.isNewCase &&
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  onClick={handleOnClickActivateDrawMode}
                >
                  <path
                    fill="#96C8FF"
                    fillRule="evenodd"
                    d="M0 4a4 4 0 0 1 4-4h16a4 4 0 0 1 4 4v16a4 4 0 0 1-4 4H4a4 4 0 0 1-4-4V4Z"
                    clipRule="evenodd"
                  />
                  <path
                    fill="#004080"
                    fillRule="evenodd"
                    d="M19.74 6.34a.885.885 0 0 1 0 1.253l-1.626 1.626-3.333-3.333 1.627-1.626a.885.885 0 0 1 1.253 0l2.08 2.08ZM4 19.999v-3.333l9.83-9.83 3.333 3.333-9.83 9.83H4Z"
                    clipRule="evenodd"
                  />
                </svg>
              }
              {/* Camera icon */}
              {/* {props.isNewCase &&} */}
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className='ml-2'
                onClick={() => {
                  props.onShowCamera({
                    formType,
                    caseDocs,
                  });
                }}
              >
                <mask id="path-1-inside-1_11323_11667" fill="white">
                  <path fillRule="evenodd" clipRule="evenodd" d="M2 0C0.895431 0 0 0.89543 0 2V22C0 23.1046 0.89543 24 2 24H22C23.1046 24 24 23.1046 24 22V2C24 0.895431 23.1046 0 22 0H2ZM4.44639 7.49989C4.44639 7.89965 4.12296 8.22308 3.7232 8.22308C3.32343 8.22308 3 7.89965 3 7.49989V4.44639C3 3.64887 3.64887 3 4.44639 3H7.49989C7.89965 3 8.22308 3.32343 8.22308 3.7232C8.22308 4.12296 7.89965 4.44639 7.49989 4.44639H4.44639V7.49989ZM4.44639 19.5501H7.49989C7.89965 19.5501 8.22308 19.8736 8.22308 20.2733C8.22308 20.6731 7.89965 20.9965 7.49989 20.9965H4.44639C3.64887 20.9965 3 20.3477 3 19.5501V16.4966C3 16.0969 3.32343 15.7734 3.7232 15.7734C4.12296 15.7734 4.44639 16.0969 4.44639 16.4966V19.5501ZM19.5532 3H16.4997C16.1 3 15.7766 3.32343 15.7766 3.7232C15.7766 4.12296 16.1 4.44639 16.4997 4.44639H19.5532V7.49989C19.5532 7.89965 19.8767 8.22308 20.2764 8.22308C20.6762 8.22308 20.9996 7.89965 20.9996 7.49989V4.44639C20.9996 3.64887 20.3508 3 19.5532 3ZM19.5531 16.4966C19.5531 16.0969 19.8765 15.7734 20.2763 15.7734C20.676 15.7734 20.9994 16.0969 20.9994 16.4966V19.5501C20.9994 20.3477 20.3506 20.9965 19.5531 20.9965H16.4996C16.0998 20.9965 15.7764 20.6731 15.7764 20.2733C15.7764 19.8736 16.0998 19.5501 16.4996 19.5501H19.5531V16.4966ZM16.2547 9.53663L12.9969 6.2496C12.9442 6.19687 12.8856 6.15234 12.8235 6.11484C12.8071 6.10547 12.7895 6.09609 12.7719 6.08789C12.7639 6.08339 12.7551 6.07958 12.7466 6.07593L12.7391 6.07265C12.6325 6.02578 12.5165 6 12.3981 6H8.34355C7.87715 6 7.49982 6.37733 7.49982 6.84373V17.156C7.49982 17.6224 7.87715 17.9997 8.34355 17.9997H15.6559C16.1223 17.9997 16.4996 17.6224 16.4996 17.156V10.1308C16.4996 9.9081 16.4117 9.69483 16.2547 9.53663ZM15.1849 9.65932H12.8436V7.29688L15.1849 9.65932ZM8.34338 17.156H15.6557V10.4999H12.8433C12.3769 10.4999 11.9995 10.1226 11.9995 9.65618V6.84375H8.34338V17.156Z" />
                </mask>
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M2 0C0.895431 0 0 0.89543 0 2V22C0 23.1046 0.89543 24 2 24H22C23.1046 24 24 23.1046 24 22V2C24 0.895431 23.1046 0 22 0H2ZM4.44639 7.49989C4.44639 7.89965 4.12296 8.22308 3.7232 8.22308C3.32343 8.22308 3 7.89965 3 7.49989V4.44639C3 3.64887 3.64887 3 4.44639 3H7.49989C7.89965 3 8.22308 3.32343 8.22308 3.7232C8.22308 4.12296 7.89965 4.44639 7.49989 4.44639H4.44639V7.49989ZM4.44639 19.5501H7.49989C7.89965 19.5501 8.22308 19.8736 8.22308 20.2733C8.22308 20.6731 7.89965 20.9965 7.49989 20.9965H4.44639C3.64887 20.9965 3 20.3477 3 19.5501V16.4966C3 16.0969 3.32343 15.7734 3.7232 15.7734C4.12296 15.7734 4.44639 16.0969 4.44639 16.4966V19.5501ZM19.5532 3H16.4997C16.1 3 15.7766 3.32343 15.7766 3.7232C15.7766 4.12296 16.1 4.44639 16.4997 4.44639H19.5532V7.49989C19.5532 7.89965 19.8767 8.22308 20.2764 8.22308C20.6762 8.22308 20.9996 7.89965 20.9996 7.49989V4.44639C20.9996 3.64887 20.3508 3 19.5532 3ZM19.5531 16.4966C19.5531 16.0969 19.8765 15.7734 20.2763 15.7734C20.676 15.7734 20.9994 16.0969 20.9994 16.4966V19.5501C20.9994 20.3477 20.3506 20.9965 19.5531 20.9965H16.4996C16.0998 20.9965 15.7764 20.6731 15.7764 20.2733C15.7764 19.8736 16.0998 19.5501 16.4996 19.5501H19.5531V16.4966ZM16.2547 9.53663L12.9969 6.2496C12.9442 6.19687 12.8856 6.15234 12.8235 6.11484C12.8071 6.10547 12.7895 6.09609 12.7719 6.08789C12.7639 6.08339 12.7551 6.07958 12.7466 6.07593L12.7391 6.07265C12.6325 6.02578 12.5165 6 12.3981 6H8.34355C7.87715 6 7.49982 6.37733 7.49982 6.84373V17.156C7.49982 17.6224 7.87715 17.9997 8.34355 17.9997H15.6559C16.1223 17.9997 16.4996 17.6224 16.4996 17.156V10.1308C16.4996 9.9081 16.4117 9.69483 16.2547 9.53663ZM15.1849 9.65932H12.8436V7.29688L15.1849 9.65932ZM8.34338 17.156H15.6557V10.4999H12.8433C12.3769 10.4999 11.9995 10.1226 11.9995 9.65618V6.84375H8.34338V17.156Z" fill="#96C8FF"
                />
                <path
                  d="M4.44639 4.44639V3.44639H3.44639V4.44639H4.44639ZM4.44639 19.5501H3.44639V20.5501H4.44639V19.5501ZM19.5532 4.44639H20.5532V3.44639H19.5532V4.44639ZM19.5531 19.5501V20.5501H20.5531V19.5501H19.5531ZM12.9969 6.2496L13.7072 5.54566L13.7041 5.5425L12.9969 6.2496ZM16.2547 9.53663L15.5444 10.2406L15.5449 10.2411L16.2547 9.53663ZM12.8235 6.11484L13.3404 5.25878L13.3301 5.25256L13.3196 5.24659L12.8235 6.11484ZM12.7719 6.08789L12.2862 6.96201L12.3171 6.97915L12.3491 6.99407L12.7719 6.08789ZM12.7466 6.07593L12.3463 6.99229L12.3491 6.99353L12.7466 6.07593ZM12.7391 6.07265L12.3367 6.98812L12.3388 6.98901L12.7391 6.07265ZM12.8436 9.65932H11.8436V10.6593H12.8436V9.65932ZM15.1849 9.65932V10.6593H17.5839L15.8952 8.95539L15.1849 9.65932ZM12.8436 7.29688L13.5538 6.59295L11.8436 4.86727V7.29688H12.8436ZM15.6557 17.156V18.156H16.6557V17.156H15.6557ZM8.34338 17.156H7.34338V18.156H8.34338V17.156ZM15.6557 10.4999H16.6557V9.49991H15.6557V10.4999ZM11.9995 6.84375H12.9995V5.84375H11.9995V6.84375ZM8.34338 6.84375V5.84375H7.34338V6.84375H8.34338ZM1 2C1 1.44772 1.44772 1 2 1V-1C0.343146 -1 -1 0.343145 -1 2H1ZM1 22V2H-1V22H1ZM2 23C1.44771 23 1 22.5523 1 22H-1C-1 23.6569 0.343145 25 2 25V23ZM22 23H2V25H22V23ZM23 22C23 22.5523 22.5523 23 22 23V25C23.6569 25 25 23.6569 25 22H23ZM23 2V22H25V2H23ZM22 1C22.5523 1 23 1.44771 23 2H25C25 0.343147 23.6569 -1 22 -1V1ZM2 1H22V-1H2V1ZM3.7232 9.22308C4.67525 9.22308 5.44639 8.45194 5.44639 7.49989H3.44639C3.44639 7.34737 3.57068 7.22308 3.7232 7.22308V9.22308ZM2 7.49989C2 8.45194 2.77115 9.22308 3.7232 9.22308V7.22308C3.87571 7.22308 4 7.34737 4 7.49989H2ZM2 4.44639V7.49989H4V4.44639H2ZM4.44639 2C3.09658 2 2 3.09658 2 4.44639H4C4 4.20115 4.20115 4 4.44639 4V2ZM7.49989 2H4.44639V4H7.49989V2ZM9.22308 3.7232C9.22308 2.77115 8.45194 2 7.49989 2V4C7.34737 4 7.22308 3.87571 7.22308 3.7232H9.22308ZM7.49989 5.44639C8.45194 5.44639 9.22308 4.67525 9.22308 3.7232H7.22308C7.22308 3.57068 7.34737 3.44639 7.49989 3.44639V5.44639ZM4.44639 5.44639H7.49989V3.44639H4.44639V5.44639ZM5.44639 7.49989V4.44639H3.44639V7.49989H5.44639ZM7.49989 18.5501H4.44639V20.5501H7.49989V18.5501ZM9.22308 20.2733C9.22308 19.3213 8.45194 18.5501 7.49989 18.5501V20.5501C7.34737 20.5501 7.22308 20.4258 7.22308 20.2733H9.22308ZM7.49989 21.9965C8.45194 21.9965 9.22308 21.2254 9.22308 20.2733H7.22308C7.22308 20.1208 7.34737 19.9965 7.49989 19.9965V21.9965ZM4.44639 21.9965H7.49989V19.9965H4.44639V21.9965ZM2 19.5501C2 20.8999 3.09658 21.9965 4.44639 21.9965V19.9965C4.20115 19.9965 4 19.7954 4 19.5501H2ZM2 16.4966V19.5501H4V16.4966H2ZM3.7232 14.7734C2.77114 14.7734 2 15.5446 2 16.4966H4C4 16.6492 3.87571 16.7734 3.7232 16.7734V14.7734ZM5.44639 16.4966C5.44639 15.5446 4.67525 14.7734 3.7232 14.7734V16.7734C3.57068 16.7734 3.44639 16.6492 3.44639 16.4966H5.44639ZM5.44639 19.5501V16.4966H3.44639V19.5501H5.44639ZM16.4997 4H19.5532V2H16.4997V4ZM16.7766 3.7232C16.7766 3.87571 16.6523 4 16.4997 4V2C15.5477 2 14.7766 2.77114 14.7766 3.7232H16.7766ZM16.4997 3.44639C16.6523 3.44639 16.7766 3.57068 16.7766 3.7232H14.7766C14.7766 4.67525 15.5477 5.44639 16.4997 5.44639V3.44639ZM19.5532 3.44639H16.4997V5.44639H19.5532V3.44639ZM20.5532 7.49989V4.44639H18.5532V7.49989H20.5532ZM20.2764 7.22308C20.429 7.22308 20.5532 7.34737 20.5532 7.49989H18.5532C18.5532 8.45194 19.3244 9.22308 20.2764 9.22308V7.22308ZM19.9996 7.49989C19.9996 7.34737 20.1239 7.22308 20.2764 7.22308V9.22308C21.2285 9.22308 21.9996 8.45194 21.9996 7.49989H19.9996ZM19.9996 4.44639V7.49989H21.9996V4.44639H19.9996ZM19.5532 4C19.7985 4 19.9996 4.20115 19.9996 4.44639H21.9996C21.9996 3.09658 20.903 2 19.5532 2V4ZM20.2763 14.7734C19.3242 14.7734 18.5531 15.5446 18.5531 16.4966H20.5531C20.5531 16.6492 20.4288 16.7734 20.2763 16.7734V14.7734ZM21.9994 16.4966C21.9994 15.5446 21.2283 14.7734 20.2763 14.7734V16.7734C20.1237 16.7734 19.9994 16.6492 19.9994 16.4966H21.9994ZM21.9994 19.5501V16.4966H19.9994V19.5501H21.9994ZM19.5531 21.9965C20.9029 21.9965 21.9994 20.8999 21.9994 19.5501H19.9994C19.9994 19.7954 19.7983 19.9965 19.5531 19.9965V21.9965ZM16.4996 21.9965H19.5531V19.9965H16.4996V21.9965ZM14.7764 20.2733C14.7764 21.2254 15.5475 21.9965 16.4996 21.9965V19.9965C16.6521 19.9965 16.7764 20.1208 16.7764 20.2733H14.7764ZM16.4996 18.5501C15.5475 18.5501 14.7764 19.3213 14.7764 20.2733H16.7764C16.7764 20.4258 16.6521 20.5501 16.4996 20.5501V18.5501ZM19.5531 18.5501H16.4996V20.5501H19.5531V18.5501ZM18.5531 16.4966V19.5501H20.5531V16.4966H18.5531ZM12.2867 6.95354L15.5444 10.2406L16.9649 8.83269L13.7072 5.54567L12.2867 6.95354ZM12.3066 6.9709C12.3071 6.97119 12.3002 6.96706 12.2898 6.95671L13.7041 5.5425C13.5882 5.42668 13.4641 5.33349 13.3404 5.25878L12.3066 6.9709ZM12.3491 6.99407C12.3416 6.99057 12.3362 6.9878 12.3331 6.98617C12.3299 6.98451 12.328 6.98342 12.3274 6.98309L13.3196 5.24659C13.2867 5.22775 13.2439 5.20462 13.1948 5.1817L12.3491 6.99407ZM12.3491 6.99353C12.3551 6.99611 12.3483 6.99323 12.3398 6.98928C12.3303 6.98492 12.3108 6.97567 12.2862 6.96201L13.2577 5.21377C13.1992 5.18127 13.1444 5.15844 13.1442 5.15834L12.3491 6.99353ZM12.3388 6.98901L12.3463 6.99229L13.147 5.15958L13.1395 5.1563L12.3388 6.98901ZM12.3981 7C12.3711 7 12.3505 6.99418 12.3367 6.98812L13.1415 5.15719C12.9145 5.05738 12.6619 5 12.3981 5V7ZM8.34355 7H12.3981V5H8.34355V7ZM8.49982 6.84373C8.49982 6.92962 8.42944 7 8.34355 7V5C7.32487 5 6.49982 5.82505 6.49982 6.84373H8.49982ZM8.49982 17.156V6.84373H6.49982V17.156H8.49982ZM8.34355 16.9997C8.42943 16.9997 8.49982 17.0701 8.49982 17.156H6.49982C6.49982 18.1747 7.32487 18.9997 8.34355 18.9997V16.9997ZM15.6559 16.9997H8.34355V18.9997H15.6559V16.9997ZM15.4996 17.156C15.4996 17.0701 15.57 16.9997 15.6559 16.9997V18.9997C16.6745 18.9997 17.4996 18.1746 17.4996 17.156H15.4996ZM15.4996 10.1308V17.156H17.4996V10.1308H15.4996ZM15.5449 10.2411C15.5162 10.2121 15.4996 10.1722 15.4996 10.1308H17.4996C17.4996 9.64399 17.3072 9.17754 16.9644 8.83215L15.5449 10.2411ZM12.8436 10.6593H15.1849V8.65932H12.8436V10.6593ZM11.8436 7.29688V9.65932H13.8436V7.29688H11.8436ZM15.8952 8.95539L13.5538 6.59295L12.1333 8.0008L14.4746 10.3632L15.8952 8.95539ZM15.6557 16.156H8.34338V18.156H15.6557V16.156ZM14.6557 10.4999V17.156H16.6557V10.4999H14.6557ZM12.8433 11.4999H15.6557V9.49991H12.8433V11.4999ZM10.9995 9.65618C10.9995 10.6749 11.8246 11.4999 12.8433 11.4999V9.49991C12.9292 9.49991 12.9995 9.57029 12.9995 9.65618H10.9995ZM10.9995 6.84375V9.65618H12.9995V6.84375H10.9995ZM8.34338 7.84375H11.9995V5.84375H8.34338V7.84375ZM9.34338 17.156V6.84375H7.34338V17.156H9.34338Z" fill="#96C8FF" mask="url(#path-1-inside-1_11323_11667)"
                />
              </svg>
              {/* Cropper icon */}
              {/* {props.isNewCase &&} */}
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none" xmlns="http://www.w3.org/2000/svg"
                className='ml-2 mr-2'
                onClick={() => {
                  props.onShowCropper({
                    formType: props.formType,
                    caseDocs: caseDocuments,
                    selectedPageId: currentPageId
                  });
                }}
              >
                <path fillRule="evenodd" clipRule="evenodd" d="M2 0C0.895431 0 0 0.89543 0 2V22C0 23.1046 0.89543 24 2 24H22C23.1046 24 24 23.1046 24 22V2C24 0.895431 23.1046 0 22 0H2ZM20 8.36364V4H15.6364V5.45455H8.36364V4H4V8.36364H5.45455V15.6364H4V20H8.36364V18.5455H15.6364V20H20V15.6364H18.5455V8.36364H20ZM15.6374 17.0881H8.3647V15.6335H6.91016V8.3608H8.3647V6.90625H15.6374V8.3608H17.092V15.6335H15.6374V17.0881Z" fill="#96C8FF" />
              </svg>
              {/* Document action menu */}
              <div
                className={`mr-2 ${css(styles.ellipsisBtn)}`}
                ref={documentDeleteMenuRef}
              >
                <i
                  className='fa fa-ellipsis-h'
                  onClick={() => {
                    setShowDocumentDeleteMenu(true);
                  }}
                />
                {showDocumentDeleteMenu &&
                  <PopupMenu
                    top={32}
                    right={1}
                    zIndex='1550'
                    position="absolute"
                    visible={showDocumentDeleteMenu}
                    parent={documentDeleteMenuRef}
                    onOutsideClick={() => { setShowDocumentDeleteMenu(false); }}
                  >
                    <div className={css(styles.searchPopupMenuContainer)}>
                      {/* Print Document */}
                      <div
                        className={`pl-2 fx fx__v-center ${css(styles.menuItem)}`}
                        onClick={() => {
                          console.log('Not implemented yet!');
                        }}
                      >
                        <div className={css(styles.menuItemIcon)} />
                        <div className={css(styles.menuItemText)}>
                          Print Document
                        </div>
                      </div>
                      {/* Fax Document */}
                      <div
                        className={`pl-2 fx fx__v-center ${css(styles.menuItem)}`}
                        onClick={() => {
                          setWarningMessage('Fax feature not implemented yet!');
                          setShowDocumentDeleteWarning(true);
                        }}
                      >
                        <div className={css(styles.menuItemIcon)} />
                        <div className={css(styles.menuItemText)}>
                          Fax Document
                        </div>
                      </div>
                      {/* Download Document */}
                      <div
                        className={`pl-2 fx fx__v-center ${css(styles.menuItem)}`}
                        onClick={() => {
                          setWarningMessage('Case document PDF download feature not implemented yet!');
                          setShowDocumentDeleteWarning(true);
                        }}
                      >
                        <div className={css(styles.menuItemIcon)} />
                        <div className={css(styles.menuItemText)}>
                          Download PDF
                        </div>
                      </div>
                      {/* Delete current page */}
                      <div
                        className={`pl-2 fx fx__v-center ${css(styles.menuItem)}`}
                        onClick={() => {
                          setWarningMessage('Do you want to delete current page from case documents?');
                          setShowPageDeleteWarning(true);
                        }}
                      >
                        {/* Icon */}
                        <div className={css(styles.menuItemIcon)}>
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path fillRule="evenodd" clipRule="evenodd" d="M23.7201 3.72416C23.5384 3.54131 23.2742 3.43938 23.0178 3.43938H18.0888V1.89413C18.0888 0.849074 17.245 0 16.2064 0H7.79253C6.75398 0 5.91019 0.849074 5.91019 1.89413V3.43938H0.956053C0.43025 3.43938 0 3.86917 0 4.40142C0 4.93367 0.427117 5.36346 0.956053 5.36346H1.79984V22.1059C1.79984 23.1509 2.64363 24 3.68219 24H20.5852C21.6237 24 22.4675 23.1509 22.4675 22.1059V5.36346H23.0173C23.5431 5.36346 24 4.93367 24 4.40142C24.0005 4.14607 23.9018 3.907 23.7201 3.72416ZM2.84502 23.6226C2.80482 23.5857 2.76513 23.5487 2.72754 23.5078C2.76461 23.5487 2.80482 23.5857 2.84502 23.6226ZM2.98535 23.6562C3.02322 23.6977 3.06177 23.7391 3.09964 23.771C3.07596 23.7511 3.0536 23.7274 3.02991 23.7023C3.01573 23.6873 3.00106 23.6718 2.98535 23.6562ZM16.1987 1.89844H7.79004V3.4542H16.1987V1.89844ZM20.5854 22.107H3.68555V5.36719H6.12084H20.5854V22.107ZM11.0244 9.56104C11.0244 9.02617 11.4541 8.59375 11.9857 8.59375C12.5172 8.59375 12.947 9.02669 12.947 9.56104V18.5357C12.947 19.0706 12.5172 19.503 11.9857 19.503C11.4541 19.503 11.0244 19.0706 11.0244 18.5357V9.56104ZM6.77963 8.59375C6.24809 8.59375 5.81836 9.02617 5.81836 9.56104V18.5357C5.81836 19.0706 6.24809 19.503 6.77963 19.503C7.31118 19.503 7.74091 19.0706 7.74091 18.5357V9.56104C7.74091 9.02669 7.31066 8.59375 6.77963 8.59375ZM16.1777 9.56104C16.1777 9.02617 16.6075 8.59375 17.139 8.59375C17.6706 8.59375 18.1003 9.02669 18.1003 9.56104V18.5357C18.1003 19.0706 17.6706 19.503 17.139 19.503C16.6075 19.503 16.1777 19.0706 16.1777 18.5357V9.56104Z" fill="#FF6060" />
                          </svg>
                        </div>
                        <div className={css(styles.menuItemText, styles.menuItemDanger)}>
                          Delete Selected Page
                        </div>
                      </div>
                      {/* Delete document */}
                      <div
                        className={`pl-2 fx fx__v-center ${css(styles.menuItem)} ${css(styles.menuItemDanger)}`}
                        onClick={() => {
                          setWarningMessage(`Do you want to delete all ${formName} pages?`);
                          setShowDocumentDeleteWarning(true);
                        }}
                      >
                        <div className={css(styles.menuItemIcon)}>
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path fillRule="evenodd" clipRule="evenodd" d="M16.2064 0H22.0711C23.1359 0 23.9973 0.861442 24 1.92621V4.39884C23.9999 4.14446 23.9012 3.90639 23.7201 3.72416C23.5384 3.54131 23.2742 3.43938 23.0178 3.43938H18.0888V1.89413C18.0888 0.849074 17.245 0 16.2064 0ZM7.79253 0H1.92621C0.861442 0 0 0.861442 0 1.92621V4.40142V22.0738C0 23.1386 0.861442 24 1.92621 24H3.67949C2.64217 23.9985 1.79984 23.15 1.79984 22.1059V5.36346H0.956053C0.427117 5.36346 0 4.93367 0 4.40142C0 3.86917 0.43025 3.43938 0.956053 3.43938H5.91019V1.89413C5.91019 0.849074 6.75398 0 7.79253 0ZM20.5878 24H22.0738C23.1386 24 24 23.1386 24 22.0738L24 4.40142C24 4.93367 23.5431 5.36346 23.0173 5.36346H22.4675V22.1059C22.4675 23.15 21.6252 23.9985 20.5878 24ZM2.84502 23.6226C2.80482 23.5857 2.76513 23.5487 2.72754 23.5078C2.76461 23.5487 2.80482 23.5857 2.84502 23.6226ZM3.09964 23.771C3.06177 23.7391 3.02322 23.6977 2.98535 23.6562C3.00106 23.6718 3.01573 23.6873 3.02991 23.7023C3.0536 23.7274 3.07596 23.7511 3.09964 23.771ZM7.79004 1.89844H16.1987V3.4542H7.79004V1.89844ZM3.68555 22.107H20.5854V5.36719H6.12084H3.68555V22.107ZM11.9857 8.59375C11.4541 8.59375 11.0244 9.02617 11.0244 9.56104V18.5357C11.0244 19.0706 11.4541 19.503 11.9857 19.503C12.5172 19.503 12.947 19.0706 12.947 18.5357V9.56104C12.947 9.02669 12.5172 8.59375 11.9857 8.59375ZM5.81836 9.56104C5.81836 9.02617 6.24809 8.59375 6.77963 8.59375C7.31066 8.59375 7.74091 9.02669 7.74091 9.56104V18.5357C7.74091 19.0706 7.31118 19.503 6.77963 19.503C6.24809 19.503 5.81836 19.0706 5.81836 18.5357V9.56104ZM17.139 8.59375C16.6075 8.59375 16.1777 9.02617 16.1777 9.56104V18.5357C16.1777 19.0706 16.6075 19.503 17.139 19.503C17.6706 19.503 18.1003 19.0706 18.1003 18.5357V9.56104C18.1003 9.02669 17.6706 8.59375 17.139 8.59375Z" fill="#FF6060" />
                          </svg>
                        </div>
                        <div className={css(styles.menuItemText)}>
                          Delete Document
                        </div>
                      </div>
                    </div>
                  </PopupMenu>
                }
              </div>
              {/* Edit document icon */}
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                onClick={handleOnEditDocument}
                style={{ display: 'none' }}
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M2 0C0.895431 0 0 0.89543 0 2V22C0 23.1046 0.89543 24 2 24H22C23.1046 24 24 23.1046 24 22V2C24 0.895431 23.1046 0 22 0H2ZM19.2152 4H13.4767C13.0423 4 12.6901 4.35179 12.6901 4.78571C12.6901 5.21964 13.0423 5.57143 13.4767 5.57143H17.2505L13.1013 9.71429C12.7938 10.0214 12.7938 10.5179 13.1013 10.825C13.255 10.9786 13.457 11.0554 13.6573 11.0554C13.8593 11.0554 14.0595 10.9786 14.2132 10.825L18.4269 6.61607V10.3554C18.4269 10.7893 18.779 11.1411 19.2135 11.1411C19.6479 11.1411 20 10.7893 20 10.3554V4.78393C20.0018 4.35179 19.6497 4 19.2152 4ZM10.5269 18.4286H6.75128L10.9006 14.2839C11.208 13.9768 11.208 13.4804 10.9006 13.1732C10.5931 12.8661 10.0961 12.8661 9.7886 13.1732L5.57318 17.3821V13.6429C5.57318 13.2089 5.221 12.8571 4.78659 12.8571C4.35218 12.8571 4 13.2089 4 13.6429V19.2143C4 19.6482 4.35218 20 4.78659 20H10.5251C10.9596 20 11.3117 19.6482 11.3117 19.2143C11.3117 18.7804 10.9613 18.4286 10.5269 18.4286Z"
                  fill="#96C8FF"
                />
              </svg>
            </div>
          </div>
          {/* Thumbnails drawer */}
          {showThumbnails &&
            <ThumbnailsPanel
              caseDocs={caseDocuments}
              activeDocId={currentPageId}
              showLoader={isUploadingCaseDocument}
              onThumbnailSelected={(id) => setCurrentPageId(id)}
              onDocumentDrop={handleOnDocumentDrop}
            />
          }
          {/* Document current page */}
          <div className={css(styles.docPageContainer)}>
            {/* <QuantuxImageViewer dataUrl={docPageImageSrc}/> */}
            <img
              ref={imageRef}
              className={css(styles.docPageImage)}
              src={docPageImageSrc}
              alt="Document Page"
            // onTouchStart={handleTouchStart}
            // onTouchMove={handleTouchMove}
            // onTouchEnd={handleTouchEnd}
            />
          </div>
        </>
      }
    </div>
  );
};

CaseDoc.defaultProps = {
  isNewCase: false
};
export default CaseDoc;
